import api from '@/services/api'

export interface Product {
  id: string
  tenantId: string
  sku: string
  slug?: string
  name: string
  description?: string | null
  priceCents: number
  currency: string
  stock: number
  isFeatured?: boolean
  imageUrl?: string | null
  keywords?: string | null
  category?: string | null
  categoryId?: string | null
  commercialRole?: string | null
  metadata?: Record<string, any> | null
}

export interface ProductCreateInput {
  sku: string
  name: string
  description?: string
  priceCents: number
  currency?: string
  stock: number
  imageUrl?: string
  keywords?: string
  category?: string
  categoryId?: string | null
  commercialRole?: string | null
  metadata?: Record<string, any> | null
}

export interface ProductUpdateInput {
  sku?: string
  name?: string
  description?: string
  priceCents?: number
  currency?: string
  stock?: number
  imageUrl?: string
  keywords?: string
  category?: string
  categoryId?: string | null
  commercialRole?: string | null
  metadata?: Record<string, any> | null
}

export async function getProducts() {
  const { data } = await api.get<{ items: any[]; total: number; page: number; pageSize: number }>(
    `/catalog/items?pageSize=100&_t=${Date.now()}`
  )
  // Map the response to include category as a string (category.name) and commercialRole
  return data.items.map((item: any) => ({
    ...item,
    category: item.category?.name || null,
    commercialRole: item.commercialRole || null,
  }))
}

export async function getProduct(id: string) {
  const { data } = await api.get<Product>(`/catalog/items/${id}`)
  return data
}

export async function createProduct(product: ProductCreateInput) {
  const { data } = await api.post<Product>('/catalog/items', product)
  return data
}

export async function updateProduct(id: string, product: ProductUpdateInput) {
  const { data } = await api.put<Product>(`/catalog/items/${id}`, product)
  return data
}

export async function deleteProduct(id: string) {
  await api.delete(`/catalog/items/${id}`)
}

export async function downloadProductTemplate() {
  const { data } = await api.get('/catalog/template', { responseType: 'blob' })
  // Create download link
  const url = window.URL.createObjectURL(new Blob([data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', 'plantilla-productos.xlsx')
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export interface ImportResult {
  jobId: string
  queuedAt: string
  totalRows?: number
}

export interface ImportStatus {
  jobId: string
  state: 'waiting' | 'active' | 'completed' | 'failed'
  result?: {
    success: boolean
    insertedCount: number
    updatedCount: number
    totalProcessed: number
    errors: string[]
    importedAt: string
  }
  progress: number
  attemptsMade: number
}

export async function importProductsExcel(file: File): Promise<ImportResult> {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await api.post<ImportResult>('/catalog/import/excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return data
}

export async function getImportStatus(jobId: string): Promise<ImportStatus> {
  const { data } = await api.get<ImportStatus>(`/catalog/import/${jobId}`)
  return data
}

export interface UploadProductImageResult {
  success: boolean
  imageUrl: string
  storageKey: string
  filename: string
  size: number
  mimetype: string
  vertical: string
}

export async function uploadProductImage(file: File): Promise<UploadProductImageResult> {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await api.post<UploadProductImageResult>('/catalog/products/images/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function uploadProductImageFromUrl(url: string): Promise<UploadProductImageResult> {
  const { data } = await api.post<UploadProductImageResult>('/catalog/products/images/from-url', { url })
  return data
}

export interface SuggestProductCommercialRoleParams {
  name: string
  description?: string
  categoryName?: string
  categoryId?: string | null
  priceCents?: number
}

export interface SuggestProductCommercialRoleResult {
  role: string
  confidence: number
  reasoning: string
  source: 'ai' | 'fallback' | 'category'
}

export async function suggestProductCommercialRole(
  params: SuggestProductCommercialRoleParams
): Promise<SuggestProductCommercialRoleResult> {
  const { data } = await api.post<SuggestProductCommercialRoleResult>('/catalog/products/suggest-role', params)
  return data
}

